import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './../glassPatternsDefs';

// Function to draw trapezoids with angled top side
// This is a 90-degree rotation of the standard trapezoid:
// - Bottom side is horizontal (full width w)
// - Left side is VERTICAL (full height h)
// - Right side is VERTICAL (full height h)
// - Top side is horizontal and ANGLED/SLANTED (shorter width, controlled by X parameter)
// orientation 'left': top edge aligned to left, angled on right
// orientation 'right': top edge aligned to right, angled on left
export function shapeAngledTopTrapezoid(svg, d, x, y, trapezoids, labelTextOrOptions = null, subFrame = false, fullCorners = false, halfCorners = false,
  displayLabel = true, labelOptions = {}, drawFrame = true) {

  // Support both old positional style and new object style
  let labelText, options;
  let innerPoints, outerPoints;
  if (typeof labelTextOrOptions === 'object' && labelTextOrOptions !== null && !Array.isArray(labelTextOrOptions)) {
    // New style: options object passed
    options = labelTextOrOptions;
    labelText = options.labelText ?? null;
    subFrame = options.subFrame ?? false;
    fullCorners = options.fullCorners ?? false;
    halfCorners = options.halfCorners ?? false;
    displayLabel = options.displayLabel ?? true;
    labelOptions = options.labelOptions ?? {};
    drawFrame = options.drawFrame ?? true;
  } else {
    // Old style: positional parameters
    labelText = labelTextOrOptions;
  }

  let currentX = x;
  let prev = {};

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  trapezoids.forEach((trapezoid, index) => {
    const {
      w, // bottom width (full width)
      h = prev.h ?? 100, // maximum height (reference height for y1/y2)
      X = prev.X ?? w / 2, // DEPRECATED: kept for backward compatibility
      Y = prev.Y ?? h / 2, // DEPRECATED: kept for backward compatibility
      y1 = prev.y1 ?? 0, // height offset for left side top point (0 = at top)
      y2 = prev.y2 ?? h / 2, // height offset for right side top point (default: middle)
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,
      glassDesign = prev.glassDesign || null,
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
      orientation = prev.orientation ?? 'right', // 'left' or 'right'
      forceSubFrame = prev.forceSubFrame || false,
      fullCorners = prev.fullCorners || false,
      halfCorners = prev.halfCorners || false,
      displayLabel = prev.displayLabel ?? true,
      middleFrame = prev.middleFrame ?? true  // Default true to show middle frame
    } = trapezoid;

    let baseFill = color;
    let tintOverlay = null;

    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white';
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }

    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign);
    }

    // Define trapezoid points based on orientation
    // Both orientations have:
    // - Bottom horizontal edge (full width w)
    // - Left vertical edge (height h)
    // - Right vertical edge (height h)
    // - Top horizontal edge (width X, angled/slanted)
    //let innerPoints, outerPoints;

    if (orientation === 'right') {
      // Right orientation: top edge is ANGLED/SLANTED
      // The trapezoid has:
      // - Bottom spans full width w at y+h (horizontal)
      // - Left side is vertical (from y+h to y+y1)
      // - Right side is vertical (from y+h to y+y2)
      // - Top is ANGLED: diagonal line from (0, y+y1) to (w, y+y2)
      //
      // Outer trapezoid (with frame extension):
      // A = bottom-left corner
      // B = top-left corner (at y+y1)
      // C = top-right corner (at y+y2)
      // D = bottom-right corner

      const A = [currentX - frameWidth, y + h + frameWidth];           // bottom-left
      const B = [currentX - frameWidth, y + y1 - frameWidth];          // top-left (at y+y1)
      const C = [currentX + w + frameWidth, y + y2 - frameWidth];      // top-right (at y+y2)
      const D = [currentX + w + frameWidth, y + h + frameWidth];       // bottom-right

      outerPoints = [A, B, C, D];

      // Helper: offset a line segment inward by distance d
      function offsetLine(P, Q, d) {
        const dx = Q[0] - P[0];
        const dy = Q[1] - P[1];
        const len = Math.hypot(dx, dy);

        // inward normal (perpendicular to the left)
        const nx = -dy / len;
        const ny = dx / len;

        return [
          [P[0] + nx * d, P[1] + ny * d],
          [Q[0] + nx * d, Q[1] + ny * d]
        ];
      }

      // Helper: intersection of two infinite lines
      function intersect(L1, L2) {
        const [A, B] = L1;
        const [C, D] = L2;

        const a1 = B[1] - A[1];
        const b1 = A[0] - B[0];
        const c1 = a1 * A[0] + b1 * A[1];

        const a2 = D[1] - C[1];
        const b2 = C[0] - D[0];
        const c2 = a2 * C[0] + b2 * C[1];

        const det = a1 * b2 - a2 * b1;

        return [
          (b2 * c1 - b1 * c2) / det,
          (a1 * c2 - a2 * c1) / det
        ];
      }

      // Offset each edge inward by frameWidth
      const AB = offsetLine(A, B, frameWidth);
      const BC = offsetLine(B, C, frameWidth);
      const CD = offsetLine(C, D, frameWidth);
      const DA = offsetLine(D, A, frameWidth);

      // Intersect offset edges to get inner trapezoid
      const A2 = intersect(AB, DA);
      const B2 = intersect(AB, BC);
      const C2 = intersect(BC, CD);
      const D2 = intersect(CD, DA);

      innerPoints = [A2, B2, C2, D2];
    }
    else { // orientation === 'left'
      // Left orientation: top edge is ANGLED/SLANTED
      // The trapezoid has:
      // - Bottom spans full width w at y+h (horizontal)
      // - Left side is vertical (from y+h to y+y1)
      // - Right side is vertical (from y+h to y+y2)
      // - Top is ANGLED: diagonal line from (0, y+y1) to (w, y+y2)
      //
      // A = bottom-left corner
      // B = top-left corner (at y+y1)
      // C = top-right corner (at y+y2)
      // D = bottom-right corner

      const A = [currentX - frameWidth, y + h + frameWidth];               // bottom-left
      const B = [currentX - frameWidth, y + y1 - frameWidth];              // top-left (at y+y1)
      const C = [currentX + w + frameWidth, y + y2 - frameWidth];          // top-right (at y+y2)
      const D = [currentX + w + frameWidth, y + h + frameWidth];           // bottom-right

      outerPoints = [A, B, C, D];

      function offsetLine(P, Q, d) {
        const dx = Q[0] - P[0];
        const dy = Q[1] - P[1];
        const len = Math.hypot(dx, dy);

        // inward normal
        const nx = -dy / len;
        const ny = dx / len;

        return [
          [P[0] + nx * d, P[1] + ny * d],
          [Q[0] + nx * d, Q[1] + ny * d]
        ];
      }

      function intersect(L1, L2) {
        const [A, B] = L1;
        const [C, D] = L2;

        const a1 = B[1] - A[1];
        const b1 = A[0] - B[0];
        const c1 = a1 * A[0] + b1 * A[1];

        const a2 = D[1] - C[1];
        const b2 = C[0] - D[0];
        const c2 = a2 * C[0] + b2 * C[1];

        const det = a1 * b2 - a2 * b1;

        return [
          (b2 * c1 - b1 * c2) / det,
          (a1 * c2 - a2 * c1) / det
        ];
      }

      // Offset each edge inward by frameWidth
      const AB = offsetLine(A, B, frameWidth);
      const BC = offsetLine(B, C, frameWidth);
      const CD = offsetLine(C, D, frameWidth);
      const DA = offsetLine(D, A, frameWidth);

      const A2 = intersect(AB, DA);
      const B2 = intersect(AB, BC);
      const C2 = intersect(BC, CD);
      const D2 = intersect(CD, DA);

      innerPoints = [A2, B2, C2, D2];
    }

    // === OUTER FRAME ===
    if (drawFrame && frameWidth > 0) {
      const outerPointsStr = outerPoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', outerPointsStr)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === BASE FILL ===
    const innerPointsStr = innerPoints.map(p => p.join(',')).join(' ');
    svg.append('polygon')
      .attr('points', innerPointsStr)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY ===
    if (tintOverlay) {
      svg.append('polygon')
        .attr('points', innerPointsStr)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === SUBFRAME (middle frame between outer frame and glass) ===
    if (drawFrame && ((subFrame === true && index === 0) || (forceSubFrame === true)) && middleFrame) {
      const subInset = frameWidth / 2; // distance from outer frame to middle frame

      // Calculate middle frame trapezoid points by offsetting from outer points
      let middlePoints;

      if (orientation === 'right') {
        const [A, B, C, D] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, subInset);
        const BC = offsetLine(B, C, subInset);
        const CD = offsetLine(C, D, subInset);
        const DA = offsetLine(D, A, subInset);

        const A2 = intersect(AB, DA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CD);
        const D2 = intersect(CD, DA);

        middlePoints = [A2, B2, C2, D2];
      } else { // left
        const [A, B, C, D] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, subInset);
        const BC = offsetLine(B, C, subInset);
        const CD = offsetLine(C, D, subInset);
        const DA = offsetLine(D, A, subInset);

        const A2 = intersect(AB, DA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CD);
        const D2 = intersect(CD, DA);

        middlePoints = [A2, B2, C2, D2];
      }

      const middlePointsStr = middlePoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', middlePointsStr)
        .attr('fill', 'none')
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel) {
      // Position label at centroid of trapezoid
      // For this trapezoid with angled top edge:
      // - Bottom is horizontal at y+h with width w
      // - Top is angled (slanted diagonal edge)
      // - Both vertical sides have height h

      let centroidX, centroidY;

      // For a trapezoid with vertical parallel sides and angled top,
      // the centroid is approximately at the center
      centroidX = currentX + w / 2;
      centroidY = y + h / 2;

      svg.append('text')
        .attr('x', centroidX)
        .attr('y', centroidY + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    currentX += w + 2 * frameWidth + d + 2 * lineFrameThickness;

    prev = {
      w, h, X, Y, y1, y2, color,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      orientation, fullCorners, halfCorners, displayLabel,
      glassType, glassDesign, middleFrame
    };
  });
  return innerPoints;
}
